import Hls from 'hls.js';
import type { Channel, LibraryItem, PlaybackTracks, StreamDescriptor } from '@shared/types.ts';
import { api, SourceBusyError } from './api.ts';
import { nativeBridge, onNativeStatus, type NativeStatus } from './native.ts';
import { bufferProfile } from '@/stores/buffer.ts';

/**
 * The player is a NON-REACT SUBSYSTEM.
 *
 * One module owns the media element and the hls.js instance, imperatively,
 * for the lifetime of the app. React renders a shell and reads status from a
 * store. This is not stylistic:
 *
 *  - An hls.js instance in useState (or created in an effect with sloppy
 *    deps) is torn down and rebuilt on renders you did not intend. Channel
 *    switching goes from ~300ms to seconds, with audio gaps.
 *  - The media element must OUTLIVE every route. If it unmounts when you
 *    navigate away from /player, audio stops — which is the single thing that
 *    makes a media app feel like a web page instead of an app.
 *
 * So: the element is created once, attached to the document, and never
 * unmounted. Routes control presentation only.
 *
 * SINCE 2026-09-10 there are TWO BACKENDS behind this one controller: hls.js
 * in a browser, and the device's own decoders when the Android wrapper is
 * present (see lib/native.ts, docs/FIELD-NOTES.md §9d). Everything above
 * this line is why the split lives HERE and nowhere else — the controller was
 * already the one imperative thing that outlives every route, so swapping
 * what decodes the bytes changes nothing about React, the stores, the
 * presentation tiers, or the CSS. The <video> element stays in the DOM even
 * on the native path, positioned by the same rules as always, because the
 * native surface is told to follow it.
 */

export type PlayerStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'error';

/** Long enough that a genuinely dead server is not hammered by recovery. */
const RECOVERY_COOLDOWN_MS = 15_000;

/**
 * How long a decode failure counts against the next one.
 *
 * Long enough that a channel failing continuously climbs the ladder within a
 * few seconds; short enough that two unrelated hiccups twenty seconds apart
 * are each treated as the first one, because they are.
 */
const DECODE_WINDOW_MS = 12_000;

/**
 * How long "Tuning…" is allowed to last before we assume it is never going to
 * finish.
 *
 * A STALL IS NOT AN ERROR, and that is the whole problem. When an upstream
 * goes quiet without closing, the player has nothing to report: no error
 * fires, the state stays BUFFERING, and every recovery path in this file —
 * all of which hang off error events — sits there waiting for an event that
 * will never come. The visible result is "Tuning…" forever, which is exactly
 * what was reported.
 *
 * Comfortably longer than the server's own cold-tune pre-roll wait (12s), so
 * a slow honest start is never mistaken for a dead one.
 */
const STALL_MS = 25_000;

/** After this many failed re-tunes, say so instead of retrying in silence. */
const MAX_RECOVERIES = 3;

export interface PlayerState {
  status: PlayerStatus;
  channel: Channel | null;
  /**
   * The library item playing, when one is.
   *
   * Alongside `channel` rather than replacing it: exactly one is ever set,
   * and everything that already reads `channel` — the chrome, the mini
   * player, now-playing — keeps working untouched rather than being rewritten
   * to understand a union it does not care about.
   */
  item: LibraryItem | null;
  /**
   * The id progress is reported against.
   *
   * Not always `item.id`: pressing a series plays an episode, and reporting
   * against the series would write somebody's position onto a thing that has
   * no position — losing the episode's place, which is the one number that
   * mattered.
   */
  playbackId: string | null;
  error: string | null;
  /** Seekable back-buffer, when the source has one (SXM has ~5 hours). */
  timeshift: StreamDescriptor['timeshift'] | null;
  /**
   * The audio tracks this item has, and which is chosen.
   *
   * Null for a live channel, which has none to choose between, and for
   * anything playing on the native bridge — the television's player reads
   * the file's own tracks and does not need telling.
   */
  tracks: PlaybackTracks | null;
  /** Playback speed. 1 is normal; only ever non-1 on a library item. */
  rate: number;
  /**
   * THE LAST FEW THINGS THAT HAPPENED, for when it goes wrong.
   *
   * This exists because three separate fixes were reasoned out from server
   * logs and none of them were the bug. The server can say what it sent; it
   * cannot say what MSE did with it, and that is the half that was failing.
   * Asking somebody to open devtools and read console lines back is a slow
   * loop that mostly does not happen — so the player keeps its own short
   * history and the error screen shows it.
   */
  trail: string[];
}

/** What to ask the server for. `startMs` keeps a re-select in place. */
export interface TrackSelection {
  audio?: number | null;
  startMs?: number;
}

type Subscriber = (state: PlayerState) => void;

class PlayerController {
  private audio: HTMLAudioElement | null = null;
  private video: HTMLVideoElement | null = null;
  private hls: Hls | null = null;
  private subscribers = new Set<Subscriber>();
  /** Guards against a slow tune landing after a newer one. */
  private tuneToken = 0;

  private state: PlayerState = {
    status: 'idle', channel: null, item: null, playbackId: null, error: null,
    timeshift: null, tracks: null, rate: 1, trail: [],
  };

  /** When we last re-tuned to recover, so recovery cannot become a loop. */
  private lastRecoveryAt = 0;
  private stallTimer: ReturnType<typeof setTimeout> | null = null;
  /** Consecutive recoveries that never reached 'playing'. */
  private recoveries = 0;
  /** Whether the CURRENT tune ever produced a frame. See the 'playing' handler. */
  private everPlayed = false;
  /** Recent decode failures, for the ladder in recoverDecode(). */
  private mediaErrors: number[] = [];

  /** Called once, from PlayerHost, with elements that never unmount. */
  attach(audio: HTMLAudioElement, video: HTMLVideoElement): void {
    this.audio = audio;
    this.video = video;

    // The native backend reports its own state; the media elements are idle
    // on that path and would never fire the events below.
    onNativeStatus((status: NativeStatus, error?: string) => {
      /*
       * A LIVE STREAM THAT "ENDS" HAS FAILED.
       *
       * The server now closes a viewer's response when its upstream drops,
       * rather than splicing a new connection into the middle of one (see
       * http/ts-broadcast.ts). That arrives here as a clean end, not an
       * error — and every recovery path in this file hangs off errors, so
       * without this the picture would simply stop and nothing would notice.
       *
       * `stop()` clears the channel first, so a deliberate stop never looks
       * like this.
       */
      if (status === 'idle' && this.state.channel) {
        if (this.tryRecover('the stream ended')) return;
        this.patch({ status: 'error', error: 'Stream ended' });
        return;
      }
      if (status !== 'error') { this.patch({ status, error: null }); return; }
      // Same recovery as the browser path: one re-tune with a fresh token
      // before the failure is shown to anybody.
      if (this.tryRecover(error ?? 'native playback error')) return;
      this.patch({ status: 'error', error: error ?? 'Playback failed' });
    });

    for (const el of [audio, video]) {
      el.addEventListener('playing', () => {
        /* THE FACT THAT CHANGES THE DIAGNOSIS. A codec MSE cannot decode
           fails on the FIRST fragment — it can never play for thirty seconds
           and then discover it cannot decode. So "did this ever play" is the
           difference between an unsupported channel and a stream that broke
           mid-flight, and the two need completely different messages. */
        this.everPlayed = true;
        this.mediaErrors = [];
        this.patch({ status: 'playing', error: null });
      });
      el.addEventListener('pause', () => {
        if (this.state.status === 'playing') this.patch({ status: 'paused' });
      });
      el.addEventListener('waiting', () => {
        if (this.state.status === 'playing') this.patch({ status: 'loading' });
      });
      el.addEventListener('error', () => {
        const err = (el as HTMLMediaElement).error;
        if (!err) return;

        /*
         * A DECODE ERROR IS NOT NECESSARILY THE END, and treating it as one
         * is why a channel that stutters once looked permanently broken.
         *
         * hls.js has its own recovery for exactly this — `recoverMediaError`
         * tears the buffer down and re-appends — but the ELEMENT's error
         * event fires first and independently, and this handler used to go
         * straight to a terminal error state. The recovery then ran
         * underneath a screen that had already given up, so whether it worked
         * was invisible.
         *
         * Now the ladder below gets its turn, and only a failure that keeps
         * happening reaches the person.
         */
        this.note(`element error ${err.code} buffer ${this.bufferAhead().toFixed(1)}s`);
        // No `this.hls` guard — see recoverDecode(). A stream playing WITHOUT
        // hls.js used to get no recovery at all: every rung was behind that
        // check, so the first decode error went straight to a dead screen.
        if (err.code === 3 && this.recoverDecode()) return;

        this.patch({
          status: 'error',
          error: mediaErrorText(err.code, this.state.channel?.kind, this.everPlayed),
        });
      });

      // A TV has no devtools; the wrapper forwards console to logcat, so this
      // is how "sound but no picture" gets diagnosed in one run instead of
      // five. videoWidth === 0 with audio playing means the video track was
      // never decoded (wrong codec / no MSE support); a real size means the
      // problem is compositing or CSS instead.
      el.addEventListener('loadedmetadata', () => {
        const v = el as HTMLVideoElement;
        console.warn(
          `[n8m] loadedmetadata ${el.tagName} ${v.videoWidth ?? 0}x${v.videoHeight ?? 0}`,
        );
      });
    }
  }

  /**
   * Re-tune the current channel to recover from a transport failure.
   *
   * The case this exists for is visible in the server log as
   * `rejected /stream/<id>: expired`, once every eleven seconds, forever: a
   * stream token outlives nothing, but a player holds the URL it was given
   * and retries the SAME dead URL until someone intervenes. Nobody was
   * intervening.
   *
   * Re-tuning asks for a fresh descriptor, which mints a fresh token, and
   * rebuilds the player around it. The cooldown is what keeps this a recovery
   * rather than a hot loop against a server that is genuinely down.
   */
  /**
   * THE MEDIA-ERROR LADDER, which is hls.js's documented recovery and was
   * only ever half-wired.
   *
   * A live IPTV channel is not one continuous encode. It is spliced — ad
   * breaks, network joins, the provider's own encoder restarting — and every
   * splice is a chance for the stream's parameters to change under a decoder
   * that has already been configured. That is what a decode error thirty
   * seconds in actually is, and it is recoverable in almost every case.
   *
   * Three rungs, because each one is more disruptive than the last:
   *   1. rebuild the buffer            — invisible when it works
   *   2. swap the audio codec, rebuild — for a splice that changed the sound
   *   3. re-tune from scratch          — a fresh descriptor and a new token
   *
   * The window is what separates "it hiccupped twice in an hour" from "it is
   * failing continuously": errors older than it are forgotten, so a channel
   * that recovers cleanly never climbs the ladder at all.
   */
  private recoverDecode(): boolean {
    const hls = this.hls;
    const now = Date.now();
    this.mediaErrors = this.mediaErrors.filter((t) => now - t < DECODE_WINDOW_MS);
    this.mediaErrors.push(now);
    const rung = this.mediaErrors.length;

    /*
     * WITHOUT hls.js THERE IS ONE RUNG, and it is the right one.
     *
     * Nothing else here is available — recoverMediaError and swapAudioCodec
     * belong to hls.js — so the only move is to ask for the stream again. On
     * a live channel that is genuinely the best recovery anyway: a fresh
     * descriptor starts at the live edge, which SKIPS the content that could
     * not be decoded rather than handing the decoder the same bytes back.
     */
    if (!hls) {
      if (this.state.channel && this.tryRecover(`decode error x${rung}`)) return true;
      this.note(`decode error x${rung}, no hls.js to recover with → giving up`);
      return false;
    }

    if (rung === 1) {
      this.note('decode error → rebuilding the buffer');
      hls.recoverMediaError();
      this.patch({ status: 'loading', error: null });
      return true;
    }

    /*
     * RUNG 2 SKIPS THE BAD CONTENT INSTEAD OF RE-APPENDING IT.
     *
     * Rung 1 tears the buffer down and feeds the same bytes back. If those
     * bytes are what the decoder choked on — a partial segment, a parameter
     * change the provider never marked — that can never succeed, and the
     * ladder just burns its rungs discovering it three times.
     *
     * On live there is somewhere better to be: the edge. Jumping there drops
     * a couple of seconds of television, which is the correct price, and it
     * is what a set-top box does with a corrupt splice.
     */
    if (rung === 2 && hls.liveSyncPosition != null && this.video) {
      this.note(`decode error again → skip to live edge (${hls.liveSyncPosition.toFixed(1)}s)`);
      hls.recoverMediaError();
      try { this.video.currentTime = hls.liveSyncPosition; } catch { /* not seekable yet */ }
      this.patch({ status: 'loading', error: null });
      return true;
    }

    if (rung === 2 || rung === 3) {
      this.note('decode error again → swapping audio codec');
      hls.swapAudioCodec();
      hls.recoverMediaError();
      this.patch({ status: 'loading', error: null });
      return true;
    }
    if (rung === 4 && this.state.channel && this.tryRecover('decode errors')) {
      return true;
    }
    this.note(`decode error x${rung} in ${DECODE_WINDOW_MS / 1000}s → giving up`);
    return false;
  }

  /** Seconds since this tune began, so the trail reads as a timeline. */
  private tuneStartedAt = 0;

  /**
   * Record one thing worth knowing, to the console AND to the trail.
   *
   * Both, deliberately: the console is richer and the television forwards it
   * to logcat, but nobody reads a browser console mid-evening. The trail is
   * the version that reaches a person without being asked for.
   */
  /** Start a fresh section of the trail without discarding what led here. */
  private noteTune(what: string): void {
    /*
     * A BOUNDARY, NOT A RESET. The trail used to be emptied on every tune —
     * including the automatic re-tune the recovery ladder performs — so the
     * events that CAUSED the recovery were erased by the recovery itself, and
     * a failure that had been through the ladder looked like a failure that
     * came out of nowhere. Keep the history; mark where the new attempt
     * begins.
     */
    const trail = [...this.state.trail, `── ${what}`].slice(-24);
    this.patch({ trail });
  }

  private note(text: string): void {
    const at = this.tuneStartedAt ? (Date.now() - this.tuneStartedAt) / 1000 : 0;
    const line = `${at.toFixed(1)}s ${text}`;
    console.warn(`[n8m] ${line}`);
    // Bounded: the last dozen events are the ones that explain a failure, and
    // an unbounded array on a screen that runs for hours is a leak.
    const trail = [...this.state.trail, line].slice(-24);
    this.patch({ trail });
  }

  /** How many seconds are buffered ahead of the playhead, or -1. */
  private bufferAhead(): number {
    const el = this.video;
    if (!el) return -1;
    for (let i = 0; i < el.buffered.length; i++) {
      if (el.buffered.start(i) <= el.currentTime && el.currentTime <= el.buffered.end(i)) {
        return el.buffered.end(i) - el.currentTime;
      }
    }
    return 0;
  }

  private tryRecover(reason: string): boolean {
    const channel = this.state.channel;
    if (!channel) return false;
    if (Date.now() - this.lastRecoveryAt < RECOVERY_COOLDOWN_MS) return false;

    this.lastRecoveryAt = Date.now();
    console.warn(`[n8m] ${reason} — re-tuning ${channel.name}`);
    void this.play(channel).catch(() => { /* reported through state */ });
    return true;
  }

  subscribe(fn: Subscriber): () => void {
    this.subscribers.add(fn);
    fn(this.state);
    return () => this.subscribers.delete(fn);
  }

  getState(): PlayerState { return this.state; }

  private patch(p: Partial<PlayerState>): void {
    this.state = { ...this.state, ...p };
    for (const s of this.subscribers) s(this.state);
    this.watchForStall();
  }

  /**
   * Notice a tune that never finishes.
   *
   * Restarted on every state change, so it measures how long the player has
   * been stuck in one place rather than how long ago it was tuned.
   */
  private watchForStall(): void {
    if (this.stallTimer) { clearTimeout(this.stallTimer); this.stallTimer = null; }

    if (this.state.status === 'playing') { this.recoveries = 0; return; }
    if (this.state.status !== 'loading' || !this.state.channel) return;

    this.stallTimer = setTimeout(() => {
      this.stallTimer = null;
      if (this.state.status !== 'loading') return;

      if (this.recoveries >= MAX_RECOVERIES) {
        // Retrying in silence forever is not resilience, it is a lie about
        // what is happening. Say it, and let OK try again.
        this.patch({ status: 'error', error: 'Stream stopped responding' });
        return;
      }
      this.recoveries++;
      this.tryRecover(`stalled for ${Math.round(STALL_MS / 1000)}s`);
    }, STALL_MS);
  }

  /**
   * Tune a channel. Resolves once playback has been requested — not once it
   * is audible; the store reports that.
   *
   * Throws SourceBusyError when SXM is already on another channel, so the
   * caller can show the "listen to what's playing / cancel" dialog.
   */
  async play(channel: Channel): Promise<void> {
    const token = ++this.tuneToken;
    this.everPlayed = false;
    this.mediaErrors = [];
    this.tuneStartedAt = Date.now();
    this.noteTune(`tune ${channel.name}`);
    /* A channel has no tracks to choose between and never plays at anything
       but normal speed, so both are cleared rather than left over from
       whatever film was on before — a Speed menu reading 1.5x over live TV
       would be a control describing something that is not happening. */
    this.patch({
      status: 'loading', channel, item: null, playbackId: null, error: null,
      timeshift: null, tracks: null, rate: 1,
    });

    let descriptor: StreamDescriptor;
    try {
      descriptor = await api.stream(channel.id);
    } catch (e) {
      if (token !== this.tuneToken) return;   // superseded by a newer tune
      if (e instanceof SourceBusyError) {
        this.patch({ status: 'idle', channel: null });
        throw e;
      }
      this.patch({ status: 'error', error: (e as Error).message });
      throw e;
    }

    if (token !== this.tuneToken) return;

    await this.attachDescriptor(descriptor, channel.kind === 'radio');
    this.applyRate();
  }

  /**
   * Play something out of the library.
   *
   * Deliberately NOT `play()` with a union argument. A channel is live and
   * infinite and cycles with left/right; a film has a length, a position
   * somebody is part-way through, and a server that wants telling where they
   * got to. They share a descriptor and a video element and almost nothing
   * else, and forcing one entry point would mean a function whose first act
   * is asking which of two things it was given.
   */
  async playItem(item: LibraryItem, select?: TrackSelection): Promise<void> {
    const token = ++this.tuneToken;
    this.everPlayed = false;
    this.mediaErrors = [];
    this.tuneStartedAt = Date.now();
    this.noteTune(`play ${item.title}`);
    this.patch({
      status: 'loading', channel: null, item, playbackId: item.id,
      error: null, timeshift: null,
      // Keep the tracks on screen across a re-select so the menu does not
      // empty and refill under the pointer that is still in it.
      tracks: select ? this.state.tracks : null,
    });

    let descriptor: StreamDescriptor;
    let resumeMs = item.userData.positionMs;
    try {
      const res = await api.libraryPlayback(item.id, select);
      descriptor = res.descriptor;
      // The server may have resolved a series or season down to an episode.
      resumeMs = res.resumeMs ?? resumeMs;
      /*
       * A RE-SELECT OVERRULES PLEX'S RESUME POSITION. Switching the audio
       * track mid-film re-requests the stream, and Plex's stored position is
       * wherever progress was last reported — up to ten seconds ago, and on
       * the transcode path possibly not at all. Using it would jump the
       * viewer backwards every time they touched the menu.
       */
      if (select?.startMs != null) resumeMs = select.startMs;
      if (res.itemId) this.patch({ playbackId: res.itemId });
      this.patch({
        tracks: { audio: res.audio ?? [], audioStreamId: res.audioStreamId ?? null },
      });
    } catch (e) {
      if (token !== this.tuneToken) return;
      this.patch({ status: 'error', error: (e as Error).message });
      throw e;
    }
    if (token !== this.tuneToken) return;

    this.teardownHls();
    this.stopElement(this.audio);
    await this.attachDescriptor(descriptor, false, resumeMs > 5_000 ? resumeMs : 0);

    this.applyRate();

    /*
     * RESUME WHERE THEY WERE. Plex knows the position and the whole reason
     * this project stayed on it was that seventeen people's positions are
     * already correct — so a film that opens at zero when Plex says 58% is
     * the feature failing quietly on its first use.
     *
     * Set after play() rather than before: seeking an element that has not
     * loaded metadata is silently ignored, which looks exactly like this not
     * working.
     */
    // The native path was handed the position above and has already used it.
    if (resumeMs > 5_000 && this.video && !nativeBridge()) {
      const el = this.video;
      const seek = () => { try { el.currentTime = resumeMs / 1000; } catch { /* not ready */ } };
      if (el.readyState >= 1) seek();
      else el.addEventListener('loadedmetadata', seek, { once: true });
    }
  }

  /**
   * Everything that happens once a descriptor is in hand.
   *
   * Split out because the library needs all of it and none of the channel
   * lookup above: a film and a television channel differ in where the
   * descriptor came from and in nothing else after that. Copying this half
   * would mean two places to fix the next time hls.js needs handling, or the
   * native bridge grows an argument — and one of them would be missed.
   */
  private async attachDescriptor(
    descriptor: StreamDescriptor, isRadio: boolean, startMs = 0,
  ): Promise<void> {
    const el = isRadio ? this.audio : this.video;
    if (!el) { this.patch({ status: 'error', error: 'Player not ready' }); return; }

    this.teardownHls();
    this.stopElement(isRadio ? this.video : this.audio);

    // The device can decode it: hand it over and let MSE stay out of the way.
    const native = nativeBridge();
    if (native) {
      this.stopElement(this.audio);
      this.stopElement(this.video);
      this.patch({ timeshift: descriptor.timeshift ?? null });

      /*
       * ABSOLUTE URL, and this is not a detail.
       *
       * Every descriptor carries a path — `/stream/<id>/...?t=<token>` —
       * because the server has no business guessing which hostname a client
       * reached it by. A browser never notices: hls.js and `el.src` both
       * resolve a relative URL against the document that asked for it.
       *
       * ExoPlayer has no document. It gets a bare path, cannot build a URI
       * from it, and dies inside its own playback loop with
       * ERROR_CODE_FAILED_RUNTIME_CHECK — which names the symptom and not one
       * thing about the cause. It failed on radio and TV alike, which is the
       * tell: nothing codec-specific fails identically on AAC and MPEG-TS.
       *
       * Resolving it here, at the boundary, keeps the wire contract honest:
       * the descriptor stays origin-agnostic and the one backend that needs
       * an absolute URL is the one that makes it.
       */
      // The buffer profile has to reach the device BEFORE it prepares, since
      // ExoPlayer's LoadControl is fixed at construction.
      const profile = bufferProfile();
      try {
        native.setBufferProfile(...profile.native, profile.nativeTargetBytes);
      } catch { /* older wrapper */ }

      /*
       * THE RESUME POSITION HAS TO COME HERE, not to the <video> element.
       *
       * On this path the page's <video> decodes nothing — it is a rectangle
       * the video surface follows — so setting currentTime on it moved a
       * player that was not playing, and a film you were halfway through
       * opened at the beginning. `playFrom` is absent on older wrappers, so
       * fall back rather than throw: starting at zero is worse than resuming
       * and much better than not playing.
       */
      const url = new URL(descriptor.url, location.origin).toString();
      if (startMs > 0 && native.playFrom) native.playFrom(url, descriptor.mimeType, startMs);
      else native.play(url, descriptor.mimeType);
      return;
    }

    const profile = bufferProfile();

    /*
     * WHICH BACKEND TOOK IT, recorded before anything can go wrong.
     *
     * A trail from a real failure came back holding ONE line: an element
     * decode error and nothing else. No manifest, no hls.js errors, no
     * recovery. That ABSENCE was the finding — every recovery path was
     * guarded on an hls.js instance, so on a stream that never got one, all
     * of it was dead code.
     *
     * `canPlayType` is the branch that decides, and it is not the constant it
     * looks like: it varies by browser, platform and version, and a "maybe"
     * sends the stream down the native path. So the answer is recorded rather
     * than assumed.
     */
    /*
     * hls.js FIRST, NATIVE ONLY AS A FALLBACK — the order was backwards.
     *
     * MEASURED, from a real failure trail: Chrome on Windows answers "maybe"
     * to canPlayType('application/vnd.apple.mpegurl'). It cannot play HLS.
     * "Maybe" is the spec's way of saying "I am not going to commit", and
     * every browser says it about things it cannot do — which is exactly why
     * canPlayType is the wrong question to LEAD with.
     *
     * Asking it first meant Chrome was handed the playlist directly, hls.js
     * was never constructed, and every recovery path in this file sat behind
     * an instance that did not exist. The stream limped along on whatever the
     * element could make of it and then died with SRC_NOT_SUPPORTED.
     *
     * The documented order is the other way round: use Media Source
     * Extensions wherever they exist, and fall back to native HLS only where
     * they do not — which in practice is Safari and iOS, the one place the
     * native player is genuinely better.
     */
    const viaHlsJs = descriptor.kind === 'hls' && Hls.isSupported();
    this.note(
      `attach ${descriptor.kind} via ${viaHlsJs ? 'hls.js' : 'native'}`
      + ` (MSE ${Hls.isSupported() ? 'yes' : 'no'},`
      + ` canPlayType "${el.canPlayType('application/vnd.apple.mpegurl') || 'no'}")`,
    );

    if (viaHlsJs) {
      // hls.js everywhere except Safari/iOS, which plays HLS natively and
      // does it better than MSE can.
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 90,

        /*
         * BUFFER OVER LATENCY. This is a deliberate trade, and the right one
         * for this app.
         *
         * A low-latency live player sits close to the edge and, when it falls
         * behind, seeks forward to catch up — which is heard as audio cutting
         * out and seen as the picture jumping. That trade makes sense for
         * sport you are watching against someone else's television. Nobody
         * here is racing anyone: being twenty seconds behind a live channel
         * is unnoticeable, and stalling is not.
         *
         * So: start further back, hold more, and let a slow segment be
         * absorbed by buffer instead of by the viewer.
         */
        /*
         * From the user's buffer profile (stores/buffer.ts), and capped there
         * at 4 segments for a reason worth repeating: our rolling window is
         * eight segments deep, and a player sitting more than half of it back
         * can slide off the BACK during a stall and start asking for segments
         * that no longer exist. It also keeps the playhead clear of the live
         * edge, which on this provider is where segments are published before
         * they have finished being written.
         */
        liveSyncDurationCount: profile.liveSync,
        maxBufferLength: profile.maxBufferSeconds,

        // A flaky link deserves more than the default three tries before a
        // fragment is given up on and becomes an audible hole.
        fragLoadingMaxRetry: 6,
        fragLoadingRetryDelay: 500,
        manifestLoadingMaxRetry: 4,
        levelLoadingMaxRetry: 4,

        // Fetch the first fragment while the manifest is still being parsed
        // instead of strictly after it. Costs one speculative request and
        // takes a whole round-trip out of time-to-first-frame, which on a
        // Fire Stick over wifi is the difference you can feel.
        startFragPrefetch: true,
      });
      this.hls = hls;
      hls.attachMedia(el as HTMLMediaElement);
      hls.loadSource(descriptor.url);

      hls.on(Hls.Events.MANIFEST_PARSED, (_e, data) => {
        const codecs = data.levels
          .map((l: any) => `${l.videoCodec ?? '-'} / ${l.audioCodec ?? '-'}`)
          .join(', ');
        this.note(`manifest ${data.levels.length} level(s) ${codecs}`);
      });

      /*
       * Buffer telemetry, forwarded to logcat by the TV wrapper.
       *
       * "It's choppy" has four possible owners — the provider, the server's
       * link to it, the server, and the wifi to the TV — and from inside the
       * app only one measurement separates them: how long a fragment took to
       * arrive versus how many seconds of playback it bought. A buffer that
       * trends down is starvation; a buffer that stays full while the picture
       * still stutters is decode.
       */
      hls.on(Hls.Events.FRAG_BUFFERED, (_e, data) => {
        const el = this.video;
        const stats: any = data.frag.stats ?? {};
        const ms = Math.round((stats.loading?.end ?? 0) - (stats.loading?.start ?? 0));
        const bytes = stats.total ?? 0;
        let ahead = 0;
        if (el) {
          for (let i = 0; i < el.buffered.length; i++) {
            if (el.buffered.start(i) <= el.currentTime && el.currentTime <= el.buffered.end(i)) {
              ahead = el.buffered.end(i) - el.currentTime;
            }
          }
        }
        const rate = ms > 0 ? ((bytes * 8) / (ms / 1000) / 1e6).toFixed(1) : '?';
        console.warn(
          `[n8m] frag ${data.frag.duration.toFixed(1)}s in ${ms}ms ` +
          `(${rate} Mbps) buffer ${ahead.toFixed(1)}s`,
        );
      });

      hls.on(Hls.Events.ERROR, (_e, data) => {
        // Non-fatal errors are exactly the events behind "the sound goes out
        // and comes back", and they are invisible without this.
        if (!data.fatal) {
          this.note(`hls ${data.details}`);
          // A 401/403 will NEVER succeed on retry — the token is dead, not
          // busy. hls.js would spend four attempts and forty seconds finding
          // that out before escalating to fatal. Re-tune now.
          const status = (data.response as any)?.code;
          if (status === 401 || status === 403) this.tryRecover(`hls ${status}`);
          return;
        }
        /*
         * SAY WHICH ONE. This branch used to act on a fatal error without
         * recording which error it was — so the single most useful fact about
         * a failure was discarded at the exact moment it was produced, and
         * three rounds of diagnosis were spent guessing at it.
         */
        this.note(`fatal ${data.type}/${data.details} buffer ${this.bufferAhead().toFixed(1)}s`);

        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          // A dead token cannot be reloaded out of — only re-issued. Try a
          // full re-tune first and fall back to a reload when the cooldown
          // says we have already just done that.
          if (!this.tryRecover(`hls ${data.details}`)) hls.startLoad();
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          if (!this.recoverDecode()) {
            this.patch({
              status: 'error',
              error: mediaErrorText(3, this.state.channel?.kind, this.everPlayed),
            });
          }
        } else {
          this.patch({ status: 'error', error: data.details ?? 'Playback failed' });
        }
      });
    } else {
      el.src = descriptor.url;
      el.load();
    }

    this.patch({ timeshift: descriptor.timeshift ?? null });

    try {
      await el.play();
    } catch (e: any) {
      // Autoplay policy: a user gesture is required before audio can start.
      // Not an error worth shouting about — the UI just needs a tap.
      if (e?.name === 'NotAllowedError') this.patch({ status: 'paused' });
      else this.patch({ status: 'error', error: e?.message ?? 'Playback failed' });
    }
  }

  pause(): void {
    const native = nativeBridge();
    if (native) { native.pause(); return; }
    this.audio?.pause();
    this.video?.pause();
  }

  resume(): void {
    const native = nativeBridge();
    if (native) { native.resume(); return; }
    const el = this.state.channel?.kind === 'radio' ? this.audio : this.video;
    void el?.play().catch(() => { /* still needs a gesture */ });
  }

  /** Is the thing playing paused right now? */
  isPaused(): boolean {
    const el = this.state.channel?.kind === 'radio' ? this.audio : this.video;
    return !!el?.paused;
  }

  togglePause(): void {
    // The native player owns its own state, so ask the status we already
    // track rather than an element that is not the one playing.
    if (nativeBridge()) {
      if (this.state.status === 'playing') this.pause(); else this.resume();
      return;
    }
    if (this.isPaused()) this.resume(); else this.pause();
  }

  /**
   * SEEKING, WHICH THIS PLAYER SIMPLY DID NOT HAVE.
   *
   * There was no way to move within a film on any platform — no skip, no
   * scrub, nothing but play from wherever it resumed. A film you cannot move
   * around in is not really watchable: you miss a line, and your only option
   * is to start it again.
   *
   * NOT ON LIVE TV, and that is not an oversight. A live channel has no
   * timeline to move along; left and right there change channel, which is the
   * cable-box reflex and already what they do.
   */
  seekable(): boolean {
    return !!this.state.item && !this.state.channel;
  }

  seekBy(deltaSeconds: number): void {
    if (!this.seekable()) return;
    const native = nativeBridge();
    if (native?.seekBy) { native.seekBy(deltaSeconds * 1000); return; }
    const el = this.video;
    if (!el || !Number.isFinite(el.duration)) return;
    const to = Math.min(Math.max(0, el.currentTime + deltaSeconds), el.duration - 0.5);
    try { el.currentTime = to; } catch { /* not ready to seek yet */ }
  }

  seekTo(positionMs: number): void {
    if (!this.seekable()) return;
    const native = nativeBridge();
    if (native?.seekTo) { native.seekTo(positionMs); return; }
    const el = this.video;
    if (!el || !Number.isFinite(el.duration)) return;
    try { el.currentTime = Math.min(Math.max(0, positionMs / 1000), el.duration - 0.5); } catch { /* not ready */ }
  }

  /** Where we are and how long it is, in ms. Zeroes when nothing is playing. */
  progress(): { positionMs: number; durationMs: number } {
    const native = nativeBridge();
    if (native?.positionMs && native?.durationMs) {
      return { positionMs: native.positionMs(), durationMs: native.durationMs() };
    }
    const el = this.video;
    if (!el) return { positionMs: 0, durationMs: 0 };
    return {
      positionMs: Math.round((el.currentTime || 0) * 1000),
      durationMs: Number.isFinite(el.duration) ? Math.round(el.duration * 1000) : 0,
    };
  }

  /** Tune the current channel again, from scratch. What OK does after a failure. */
  retry(): void {
    const channel = this.state.channel;
    if (!channel) return;
    this.recoveries = 0;
    this.lastRecoveryAt = 0;
    void this.play(channel).catch(() => { /* surfaced in state */ });
  }

  stop(): void {
    this.tuneToken++;
    if (this.stallTimer) { clearTimeout(this.stallTimer); this.stallTimer = null; }
    this.recoveries = 0;
    this.teardownHls();
    nativeBridge()?.stop();
    this.stopElement(this.audio);
    this.stopElement(this.video);
    this.patch({
      status: 'idle', channel: null, item: null, playbackId: null, error: null,
      timeshift: null, tracks: null, rate: 1,
    });
  }

  /**
   * How far behind the live edge the audio actually is, in milliseconds.
   *
   * Needed because now-playing metadata describes the LIVE edge while the
   * listener is a buffer behind it — reported as the artist and song being
   * about fourteen seconds early, which is exactly one liveSyncDurationCount
   * of this station's segments. Nothing was wrong with the metadata; it was
   * being shown on the wrong clock.
   *
   * Zero when the player cannot say, which reverts to the old behaviour
   * rather than guessing.
   */
  latencyMs(): number {
    const native = nativeBridge();
    if (native) {
      try { return Math.max(0, native.latencyMs()); } catch { return 0; }
    }
    const seconds = (this.hls as any)?.latency;
    return typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0
      ? Math.round(seconds * 1000)
      : 0;
  }

  /**
   * CHANGE THE AUDIO TRACK, WHICH MEANS RE-REQUESTING THE STREAM.
   *
   * Not a local switch, and it cannot be one: Chrome exposes no API for
   * choosing which audio stream a file plays. So the server is asked again
   * with the choice, and applies it upstream in the transcoder — which is
   * why this is the one control here that costs the house server something.
   *
   * THE POSITION IS CARRIED ACROSS. Anything else and switching the audio
   * forty minutes into a film starts it again from the beginning, which is
   * the kind of thing that stops people using a feature after one go.
   */
  async selectAudio(id: number): Promise<void> {
    const item = this.state.item;
    if (!item) return;
    await this.playItem(item, { audio: id, startMs: this.progress().positionMs });
  }

  /**
   * Speed, which IS local and costs nothing.
   *
   * The one control here that needs no server at all: it is a property of the
   * media element. Kept in player state anyway so a menu can show a tick
   * against the current value without reaching for the DOM.
   */
  setRate(rate: number): void {
    this.patch({ rate });
    this.applyRate();
  }

  private applyRate(): void {
    const rate = this.state.rate;
    for (const el of [this.video, this.audio]) {
      if (!el) continue;
      try { el.playbackRate = rate; } catch { /* some sources refuse */ }
    }
  }

  setVolume(v: number): void {
    nativeBridge()?.setVolume(v);
    if (this.audio) this.audio.volume = v;
    if (this.video) this.video.volume = v;
  }

  private teardownHls(): void {
    if (!this.hls) return;
    try { this.hls.destroy(); } catch { /* already gone */ }
    this.hls = null;
  }

  private stopElement(el: HTMLMediaElement | null): void {
    if (!el) return;
    try {
      el.pause();
      el.removeAttribute('src');
      el.load();
    } catch { /* already torn down */ }
  }
}

/**
 * WHAT HAPPENED, NOT WHAT I THINK CAUSED IT.
 *
 * The previous version of this said, for any decode error on a TV channel,
 * "this channel uses audio or video a browser cannot decode — it plays on the
 * TV app". That is a DIAGNOSIS, it was stated with certainty, and it is wrong
 * in the commonest case. It cost two days of chasing codecs that were never
 * the problem.
 *
 * THE FACT THAT SEPARATES THEM IS WHETHER IT EVER PLAYED. MSE rejects a codec
 * it cannot decode on the FIRST fragment — nothing is ever rendered. So:
 *
 *   never played   the codec really is unsupported, and the TV app is the
 *                  honest answer.
 *   played, then
 *   failed         the stream broke mid-flight. Live IPTV is spliced at ad
 *                  breaks and encoder restarts, and a splice can change the
 *                  parameters under a decoder that is already configured.
 *                  That is recoverable, and by the time this text is reached
 *                  the ladder in recoverDecode() has already tried three
 *                  times — so what this says is "it kept happening", which is
 *                  a fault to investigate rather than a limit to accept.
 */
function mediaErrorText(
  code: number,
  kind?: 'tv' | 'radio' | string | null,
  everPlayed = false,
): string {
  switch (code) {
    case 1: return 'Playback aborted';
    case 2: return 'Network error';
    case 3:
      if (everPlayed) {
        return 'The stream broke mid-play and could not be recovered. '
          + 'Usually a bad patch from the provider — try tuning again.';
      }
      return kind === 'tv'
        ? 'This channel uses audio or video a browser cannot decode. It plays on the TV app.'
        : 'Could not decode the stream';
    case 4: return 'This stream format is not supported';
    default: return 'Playback failed';
  }
}

export const player = new PlayerController();
